import React from 'react'
import {FormattedMessageWrapper} from '@daedalus/core/src/localization/components/FormattedMessage'
import styled from '@emotion/styled'
import {css} from '@emotion/core'
import {test} from 'ramda'
import Edit from '../../../../foundation/icons/Edit'
import {useTheme} from 'emotion-theming'
import Heading from '../../../designSystem/Heading'
import Text from '../../../designSystem/Text'
import Tag from '../../../designSystem/Tag'
import Avatar, {AvatarType} from '../../../designSystem/Avatar'
import {isAuth0PhoneNumberFallbackPicture} from '@daedalus/core/src/auth/business/authentication'
import {getUserName} from '@daedalus/core/src/user/business'
import {useDeviceLayout} from '../../../../context/deviceLayout'
import PlusTextTag from '../../privateDeals/PrivateDealTag/PlusTextTag'
import {
  useAccountUrl,
  useCognitoUser,
  useIsMemberPlus,
  useLogOut
} from '@daedalus/core/src/auth/modules/useCognito'
import {toggle} from 'opticks/lib/optimizely'
import {useAuth} from '@daedalus/core/src/auth/components/AuthProvider'

const ICON_SIZE = 35
const ICON_BACKGROUND_SIZE = 72

const ArrowWrapper = styled('div')(
  ({theme}) => css`
    color: ${theme.colors.textInputIconDefault};
    text-align: center;
    width: ${theme.layout.spacing.s600}px;
    height: 100%;
    & svg {
      /* @mirror */
    }
  `
)

const RowLink = styled('a')(
  ({theme}) => css`
    display: flex;
    align-items: center;
    padding: ${theme.layout.spacing.s600}px 0;
    justify-content: space-between;
    align-items: flex-start;

    :hover,
    :visited {
      text-decoration: none;
      color: inherit;
    }
  `
)

const LinkElement = styled('a')(
  ({theme}) => css`
    display: block;
    padding: ${theme.layout.spacing.s500}px;
    line-height: ${theme.fonts.lineHeight.md};
    border-bottom: solid 1px ${theme.colors.profileLinksBorder};

    &:last-of-type {
      border-bottom: none;
    }
  `
)

const AvatarWrapper = styled('div')<{isMemberPlus: boolean}>(
  ({theme, isMemberPlus}) => css`
    margin-right: ${theme.layout.spacing.s500}px;
    position: relative;
    &:before {
      ${isMemberPlus ? 'content: "";' : ''}
      position: absolute;
      left: -6px;
      right: -6px;
      top: -6px;
      bottom: -6px;
      border: 1px solid ${theme.colors.violet.v300};
      border-radius: 50%;
    }
  `
)

const UserInfo = styled('div')(
  () => css`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
  `
)

const useToggleAuth = () => {
  const auth0Values = useAuth()
  const logOut = useLogOut()
  const user = useCognitoUser()
  const accountUrl = useAccountUrl()
  const isMemberPlus = useIsMemberPlus()

  return toggle('f54ef0b3-cognito-auth', auth0Values, {
    logOut,
    user,
    accountUrl,
    isMemberPlus
  })
}

const ProfileLinks = () => {
  const {isMobile} = useDeviceLayout()
  const {logOut, user, accountUrl, isMemberPlus} = useToggleAuth()
  const {colors} = useTheme()

  const isPhoneNumber = test(/^\+/, user?.name)

  const handleLogOutClick = () => {
    if (logOut) logOut()
  }

  return isMobile ? (
    <RowLink href={accountUrl}>
      <UserInfo>
        <AvatarWrapper isMemberPlus={isMemberPlus}>
          <Avatar
            picture={user?.picture}
            name={getUserName(user)}
            size={ICON_BACKGROUND_SIZE}
            iconSize={ICON_SIZE}
            type={
              isAuth0PhoneNumberFallbackPicture(user?.picture)
                ? AvatarType.Icon
                : AvatarType.Picture
            }
          />
        </AvatarWrapper>

        <div>
          <Heading appearance="title3" marginBottomSize="s000">
            {isPhoneNumber ? (
              <>
                <FormattedMessageWrapper
                  id="userWidget.enterYourName"
                  defaultMessage="Enter your name"
                />
                <Text appearance="subtitle2" colorPath="midnight.m500">
                  {user?.name}
                </Text>
              </>
            ) : (
              <FormattedMessageWrapper
                id="userWidget.greeting"
                defaultMessage="Hi, {user}"
                values={{
                  user: user?.name
                }}
              />
            )}
          </Heading>
          <Text appearance="subtitle2" colorPath="midnight.m500">
            {user?.email}
          </Text>
          {isMemberPlus ? (
            <PlusTextTag>
              <FormattedMessageWrapper
                id="tierProgression.member"
                defaultMessage="Member"
              />
            </PlusTextTag>
          ) : (
            <Tag
              textColor={colors.privateDeal}
              id="verifiedMember"
              backgroundColor={colors.privateDealAccountTag}
            >
              <FormattedMessageWrapper
                id="userWidget.verifiedMember"
                defaultMessage="Verified member"
              />
            </Tag>
          )}
        </div>
      </UserInfo>

      <ArrowWrapper>
        <Edit />
      </ArrowWrapper>
    </RowLink>
  ) : (
    <>
      <LinkElement href={accountUrl}>
        <FormattedMessageWrapper
          dataId="AccountProfileLink"
          id="account"
          defaultMessage="Account"
        />
      </LinkElement>
      <LinkElement onClick={handleLogOutClick}>
        <FormattedMessageWrapper
          dataId="LogOutProfileLink"
          id="logOut"
          defaultMessage="Log out"
        />
      </LinkElement>
    </>
  )
}

export default ProfileLinks
